import React from 'react'
import { match } from 'ts-pattern'
import Text from '../text'
import Badge, { BadgeProps } from '../badge'
import classnames from 'classnames/bind'
import { ColorNames } from '../../../constants/colorVars'
import PopupTooltip from '../../overlays/popupTooltip'
import Icon from '../icon'
import { getCssColorFromColorName, lightenColor } from '../../../utils/colors'
import { AutomationTarget } from '../../../../src/types'

import styles from './styles.scss'
const cx = classnames.bind(styles)

type RestrictedColorNames =
  | ColorNames.Gray
  | ColorNames.Info
  | ColorNames.Success
  | ColorNames.Caution
  | ColorNames.Error
  | ColorNames.Purple

export interface StatusMessageProps extends AutomationTarget {
  className?: string
  style?: React.CSSProperties
  color?: RestrictedColorNames
  title: string
  description: string
  badgeText?: string
  badgeLabel?: string
  tooltipText?: string
}

const StatusMessage: React.FC<StatusMessageProps> = ({
  className,
  elmId = 'status-message',
  color: colorName = ColorNames.Gray,
  style,
  title,
  description,
  badgeText,
  badgeLabel,
  tooltipText,
}) => {
  const backgroundColor = match<RestrictedColorNames, ColorNames>(colorName)
    .with(ColorNames.Gray, () => ColorNames.GrayExtraLight)
    .with(ColorNames.Info, () => ColorNames.InfoExtraLight)
    .with(ColorNames.Success, () => ColorNames.SuccessExtraLight)
    .with(ColorNames.Caution, () => ColorNames.CautionExtraLight)
    .with(ColorNames.Error, () => ColorNames.ErrorExtraLight)
    .with(ColorNames.Purple, () => ColorNames.PurpleExtraLight)
    .exhaustive()

  const rawForegroundColor = getCssColorFromColorName(colorName)
  const rawBackgroundColor = getCssColorFromColorName(backgroundColor)
  const rawBorderColor = lightenColor(rawForegroundColor, 70)

  return (
    <div
      className={cx('status-message', className)}
      data-elm-id={elmId}
      style={{
        backgroundColor: rawBackgroundColor,
        borderColor: rawBorderColor,
        ...style,
      }}
    >
      <div className={cx('left')} data-elm-id={`${elmId}-left`}>
        <div className={cx('tooltip-icon')}>
          <Text size='tiny' weight='heavy' color={colorName}>
            {title}
          </Text>
          {tooltipText && (
            // @ts-ignore needs to be ts
            <PopupTooltip
              on='hover'
              trigger={
                <Icon
                  name='info-circle'
                  iconStyle='light'
                  color='gray'
                  size='small'
                />
              }
            >
              {tooltipText}
            </PopupTooltip>
          )}
        </div>
        <Text size='tiny' color={ColorNames.Gray}>
          {description}
        </Text>
      </div>
      <div className={cx('right')} data-elm-id={`${elmId}-right`}>
        <Text size='tiny' color={ColorNames.Gray}>
          {badgeLabel}
        </Text>
        {badgeText && (
          <Badge
            className={cx('badge')}
            primary={badgeText}
            secondary=''
            mode={colorName as BadgeProps['mode']}
            size='small'
          />
        )}
      </div>
    </div>
  )
}

export default StatusMessage
